export type ClientErrorKind = 'render' | 'window' | 'promise' | 'api' | 'network'

export type ClientErrorInput = {
  kind: ClientErrorKind
  message: string
  stack?: string | null
  apiPath?: string | null
  apiStatus?: number | null
}

const SINK_PATH = '/api/v1/client-errors'
const SEND_CAP_PER_TAB = 20
const DEDUPE_WINDOW_MS = 60_000
const MAX_MESSAGE = 500
const MAX_STACK = 4000

// One id per page load: groups the burst of errors belonging to a single
// episode, so ten reports read as one incident rather than ten.
let sessionRef = randomRef()
let sent = 0
const lastSentAt = new Map<string, number>()

// The last token `apiFetch` obtained. Held here rather than fetched on demand
// because a report must stay synchronous: awaiting Clerk would race the
// navigation that `keepalive` exists to survive. A stale token costs nothing —
// intake treats identity as a nicety and stores the report either way.
let authToken: string | null = null

/**
 * Hand telemetry the current Clerk token so a report can be attributed to a
 * user. Called by `apiFetch` on every request; pass null when there is none.
 */
export function setTelemetryToken(token: string | null): void {
  authToken = token
}

function randomRef(): string {
  return Math.random().toString(36).slice(2, 12)
}

// The bundle filename ties a minified stack to the right sourcemap. Read it off
// the loaded script rather than baking in a build-time constant, so it stays
// correct no matter which build the browser actually has cached.
export function currentBundle(): string | null {
  try {
    const scripts = Array.from(document.querySelectorAll<HTMLScriptElement>('script[src]'))
    const app = scripts.map((s) => s.src).find((src) => /\/assets\/index-[^/]+\.js$/.test(src))
    return app ? (app.split('/').pop() ?? null) : null
  } catch {
    return null
  }
}

/**
 * Fire-and-forget error report. Never throws and never rejects: a failure to
 * report an error must not itself become an error, or the app would recurse.
 */
export function reportClientError(input: ClientErrorInput): void {
  try {
    if (sent >= SEND_CAP_PER_TAB) return

    const key = `${input.kind}|${input.message}|${input.apiPath ?? ''}`
    const now = Date.now()
    const previous = lastSentAt.get(key)
    if (previous != null && now - previous < DEDUPE_WINDOW_MS) return
    lastSentAt.set(key, now)
    sent += 1

    const body = JSON.stringify({
      kind: input.kind,
      message: String(input.message).slice(0, MAX_MESSAGE),
      stack: input.stack ? String(input.stack).slice(0, MAX_STACK) : null,
      route: typeof location === 'undefined' ? null : location.pathname,
      apiPath: input.apiPath ?? null,
      apiStatus: input.apiStatus ?? null,
      bundle: currentBundle(),
      sessionRef,
    })

    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (authToken) headers.Authorization = `Bearer ${authToken}`

    // keepalive so a report survives the navigation or reload that usually
    // follows a crash.
    void fetch(SINK_PATH, {
      method: 'POST',
      headers,
      keepalive: true,
      body,
    })?.catch(() => {})
  } catch {
    // Telemetry is best-effort by definition.
  }
}

/** Test-only: clears the per-tab cap, the dedupe memory, the session id and the token. */
export function resetTelemetryForTests(): void {
  sent = 0
  lastSentAt.clear()
  sessionRef = randomRef()
  authToken = null
}
